import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Typography from '@material-ui/core/Typography';
import { TimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment, { Moment, MomentInput } from 'moment';

import { ClockIcon, colors } from '../../../assets';
import { USADateTimeFormat } from '../../../utils';
import { Button } from '../../button/Button';
import { ConfirmationTooltip } from '../../confirmationTooltip/confirmationTooltip';
import { Paper } from '../../paper/Paper';
import { useStyles } from './ClockTimepicker.styles';
import { ClockTimepickerProps } from './ClockTimepicker.types';

export const ClockTimepicker = memo(
  ({
    value,
    defaultValue,
    format = 'HH:mm',
    inputStyledButton,
    placement,
    disabled,
    buttonClasses,
    size = 'medium',
    maxDate,
    maxDateErrorText,
    minDate,
    minDateErrorText,
    settings,
    handleConfirm,
    ...rest
  }: ClockTimepickerProps) => {
    const styles = useStyles({ disabled, size });

    // Starts as the `value` string; holds the picker's Moment once a time has been picked.
    const [selectedTime, setSelectedTime] = useState<string | Moment | null | undefined>(
      () => value || (settings?.ignoreDefaultValueForSelectedTime ? undefined : defaultValue),
    );
    const [state, setState] = useState({
      isFuture: false,
      isPast: false,
    });

    const checkDate = useCallback(
      (date: MomentInput) => {
        const errors = {
          isFuture: false,
          isPast: false,
        };

        if (maxDate && maxDate.isBefore(date, 'minutes')) {
          errors.isFuture = true;
        }

        if (minDate && minDate.isAfter(date, 'minutes')) {
          errors.isPast = true;
        }

        setState(errors);
      },
      [minDate, maxDate],
    );

    const handleTimeChange = useCallback(
      (dateTime: MaterialUiPickersDate) => {
        checkDate(dateTime);
        setSelectedTime(dateTime);
      },
      [checkDate],
    );

    const handleClose = useCallback(() => {
      setSelectedTime(value);
    }, [value, setSelectedTime]);

    useEffect(() => {
      if (value) {
        checkDate(moment(value));
        setSelectedTime(value);
      }
    }, [value, checkDate]);

    const valueAsText = useMemo(
      () => (selectedTime ? moment(selectedTime).format(format) : 'Select'),
      [format, selectedTime],
    );

    const errorText = useMemo(() => {
      if (maxDate && state.isFuture) {
        return maxDateErrorText || `Time should be before MAX date: (${maxDate.format(USADateTimeFormat)})`;
      }

      if (minDate && state.isPast) {
        return minDateErrorText || `Time should be before MIN date: (${minDate.format(USADateTimeFormat)})`;
      }

      return;
    }, [maxDate, maxDateErrorText, minDate, minDateErrorText, state.isFuture, state.isPast]);

    return (
      <ConfirmationTooltip
        toggle={
          inputStyledButton ? (
            <Paper variant={!disabled ? 'outlined' : 'elevation'}>
              <div className={styles.customButton}>
                <ClockIcon />

                <div className={styles.valueText}>
                  <Typography variant="h5" component="span" color="secondary">
                    {valueAsText}
                  </Typography>
                </div>
              </div>
            </Paper>
          ) : (
            <Button
              buttonType="outlined"
              startIcon={<ClockIcon color={!disabled ? colors.primary.main : colors.grey120} />}
              disabled={disabled}
              classes={buttonClasses}
              className={styles.button}
            >
              {valueAsText}
            </Button>
          )
        }
        fullWidthButton={inputStyledButton}
        placement={placement}
        zIndex={1300}
        disabled={disabled}
        confirmationDisabled={!selectedTime || state.isFuture || state.isPast}
        onConfirm={() => {
          if (selectedTime) {
            handleConfirm(selectedTime);
          }
        }}
        onClose={handleClose}
      >
        <TimePicker
          {...rest}
          variant="static"
          // valueOf() is what `new Date(moment)` would read anyway; strings pass through unchanged.
          value={new Date((selectedTime || defaultValue || '').valueOf())}
          onChange={handleTimeChange}
          disabled={disabled}
        />

        {errorText && (
          <div className={styles.error}>
            <Typography color="inherit" variant="subtitle1" align="center">
              {errorText}
            </Typography>
          </div>
        )}
      </ConfirmationTooltip>
    );
  },
);
